import React, { useState, useEffect } from 'react';
import { FiArrowLeft, FiShoppingCart, FiExternalLink } from 'react-icons/fi';
import styled from 'styled-components';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Styled components for the book detail page
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  padding: 20px 0;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 2px solid #e5e7eb;
  color: #374151;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 30px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background-color: #f8fafc;
  }
`;

const BookDetailContainer = styled.div`
  display: grid;
  grid-template-columns: 400px 1fr;
  gap: 40px;
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
    padding: 30px 20px;
  }
`;

const BookImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BookImage = styled.img`
  width: 100%;
  max-width: 350px;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  margin-bottom: 20px;
  background: #f3f4f6;
  min-height: 400px;
  object-fit: cover;
`;

const BookInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const BookTitle = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 16px;
  line-height: 1.3;
`;

const BookMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetaLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetaValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 30px;
`;

const CurrentPrice = styled.span`
  font-size: 2rem;
  font-weight: 700;
  color: #059669;
`;

const OriginalPrice = styled.span`
  font-size: 1.2rem;
  color: #6b7280;
  text-decoration: line-through;
`;

const DiscountBadge = styled.span`
  background: #fef3c7;
  color: #92400e;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
`;

const Description = styled.div`
  font-size: 16px;
  line-height: 1.7;
  color: #374151;
  margin-bottom: 30px;
  
  h1, h2, h3, h4, h5, h6 {
    color: #1f2937;
    margin: 20px 0 10px 0;
  }
  
  p {
    margin-bottom: 15px;
  }
  
  ul, ol {
    margin-left: 20px;
    margin-bottom: 15px;
  }
`;

const VideoSection = styled.div`
  margin-bottom: 30px;
`;

const VideoTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 15px;
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const PurchaseSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 30px;
`;

const PurchaseButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  
  &.primary {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    color: white;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    }
  }
  
  &.amazon {
    background: linear-gradient(135deg, #ff9900 0%, #ff7700 100%);
    color: white;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(255, 153, 0, 0.3);
    }
  }
  
  &.barnes {
    background: linear-gradient(135deg, #006633 0%, #004422 100%);
    color: white;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 102, 51, 0.3);
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 18px;
  color: #6b7280;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 18px;
  color: #dc2626;
  background: #fef2f2;
  border-radius: 12px;
  padding: 40px;
`;

interface Book {
  id: number;
  title: string;
  description: string;
  short_description?: string;
  image: string;
  price: string;
  discount_price?: string;
  video_link: string;
  file?: string;
  referal_link?: string;
  other_link?: string;
  subject: string;
  grade: string;
  vendor: string;
  vendor_id: number;
  vendorInfo?: {
    vendor_id: number;
    vendorlist: string;
    active: boolean;
  };
  created_at: string;
  updated_at: string;
}

interface BookDetailPageProps {
  bookId: string | null;
  onHomeClick: () => void;
  onBooksClick: () => void;
  onVideoLearningClick: () => void;
  onBlogsClick: () => void;
  onLoginClick: () => void;
  onAdminClick: () => void;
  onMkAcademyOnlineClick: () => void;
  onMkAcademyInPersonClick: () => void;
  onFaqClick: () => void;
  onFeedbackClick: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: any;
  onLogout: () => void;
}

const BookDetailPage: React.FC<BookDetailPageProps> = ({
  bookId,
  onHomeClick,
  onBooksClick,
  onVideoLearningClick,
  onBlogsClick,
  onLoginClick,
  onAdminClick,
  onMkAcademyOnlineClick,
  onMkAcademyInPersonClick,
  onFaqClick,
  onFeedbackClick,
  isAuthenticated,
  isAdmin,
  user,
  onLogout
}) => {
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Get API configuration
  const getApiConfig = () => {
    return {
      bookService: process.env.REACT_APP_BOOK_SERVICE_URL!
    };
  };

  const apiConfig = getApiConfig();

  useEffect(() => {
    if (bookId) {
      fetchBookDetail(bookId);
    } else {
      setError('No book ID provided');
      setLoading(false);
    }
  }, [bookId]);

  const fetchBookDetail = async (bookId: string) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 Fetching book detail for ID:', bookId);
      console.log('🌐 API URL:', `${apiConfig.bookService}/api/books/${bookId}`);
      
      const response = await fetch(`${apiConfig.bookService}/api/books/${bookId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.book) {
        console.log('📚 Book detail loaded:', data.book);
        console.log('🖼️ Book image filename from API:', data.book.image);
        console.log('🖼️ Complete image URL will be:', `/images/${data.book.image}`);
        console.log('🆔 Book ID from API:', data.book.id);
        console.log('📖 Book title from API:', data.book.title);
        setBook(data.book);
      } else {
        console.log('❌ Book API response:', data);
        throw new Error('Book not found');
      }
    } catch (err) {
      console.error('Error fetching book detail:', err);
      setError(err instanceof Error ? err.message : 'Failed to load book details');
    } finally {
      setLoading(false);
    }
  };

  const calculateDiscount = (price: string, discountPrice?: string) => {
    if (!discountPrice) return null;
    const original = parseFloat(price);
    const discounted = parseFloat(discountPrice);
    const discount = Math.round(((original - discounted) / original) * 100);
    return discount > 0 ? discount : null;
  };

  const renderPurchaseButtons = () => {
    if (!book) return null;

    const buttons = [];

    // Handle multiple vendors if vendorInfo exists
    if (book.vendorInfo?.vendorlist) {
      const vendors = book.vendorInfo.vendorlist.split(',').map(v => v.trim().toLowerCase());
      
      vendors.forEach(vendor => {
        if (vendor === 'mathknots') {
          buttons.push(
            <PurchaseButton
              key="mathknots"
              className="primary"
              onClick={() => {
                console.log('Adding book to cart:', book.title);
                alert(`Added "${book.title}" to cart!`);
              }}
            >
              <FiShoppingCart size={18} />
              Add to Cart
            </PurchaseButton>
          );
        } else if (vendor === 'amazon') {
          buttons.push(
            <PurchaseButton
              key="amazon"
              className="amazon"
              onClick={() => {
                if (book.referal_link) {
                  window.open(book.referal_link, '_blank');
                } else {
                  console.log('No Amazon link available');
                }
              }}
            >
              <FiExternalLink size={18} />
              Buy on Amazon
            </PurchaseButton>
          );
        } else if (vendor === 'barnesandnoble') {
          buttons.push(
            <PurchaseButton
              key="barnes"
              className="barnes"
              onClick={() => {
                if (book.other_link) {
                  window.open(book.other_link, '_blank');
                } else {
                  console.log('No Barnes & Noble link available');
                }
              }}
            >
              <FiExternalLink size={18} />
              Buy on B&N
            </PurchaseButton>
          );
        }
      });
    } else {
      // Fallback to single vendor based on vendor_id
      if (book.vendor_id === 1) { // mathknots
        buttons.push(
          <PurchaseButton
            key="mathknots"
            className="primary"
            onClick={() => {
              console.log('Adding book to cart:', book.title);
              alert(`Added "${book.title}" to cart!`);
            }}
          >
            <FiShoppingCart size={18} />
            Add to Cart
          </PurchaseButton>
        );
      } else if (book.vendor_id === 2) { // amazon
        buttons.push(
          <PurchaseButton
            key="amazon"
            className="amazon"
            onClick={() => {
              if (book.referal_link) {
                window.open(book.referal_link, '_blank');
              }
            }}
          >
            <FiExternalLink size={18} />
            Buy on Amazon
          </PurchaseButton>
        );
      } else if (book.vendor_id === 3) { // barnes & noble
        buttons.push(
          <PurchaseButton
            key="barnes"
            className="barnes"
            onClick={() => {
              if (book.other_link) {
                window.open(book.other_link, '_blank');
              }
            }}
          >
            <FiExternalLink size={18} />
            Buy on B&N
          </PurchaseButton>
        );
      }
    }

    return buttons;
  };

  if (loading) {
    return (
      <PageContainer>
        <ContentContainer>
          <LoadingContainer>Loading book details...</LoadingContainer>
        </ContentContainer>
      </PageContainer>
    );
  }

  if (error || !book) {
    return (
      <PageContainer>
        <ContentContainer>
          <BackButton onClick={onBooksClick}>
            <FiArrowLeft size={16} />
            Back to Books
          </BackButton>
          <ErrorContainer>
            {error || 'Book not found'}
          </ErrorContainer>
        </ContentContainer>
      </PageContainer>
    );
  }

  const discount = calculateDiscount(book.price, book.discount_price);

  return (
    <>
    <PageContainer>
      <Header 
        onHomeClick={onHomeClick}
        onBooksClick={onBooksClick}
        onVideoLearningClick={onVideoLearningClick}
        onBlogsClick={onBlogsClick}
        onLoginClick={onLoginClick}
        onAdminClick={onAdminClick}
        onMkAcademyOnlineClick={onMkAcademyOnlineClick}
        onMkAcademyInPersonClick={onMkAcademyInPersonClick}
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        user={user}
        onLogout={onLogout}
      />
      <ContentContainer>
        <BackButton onClick={onBooksClick}>
          <FiArrowLeft size={16} />
          Back to Books
        </BackButton>

        <BookDetailContainer>
          {/* Left Column - Book Image */}
          <BookImageContainer>
            <BookImage
              src={`/images/${book.image}`}
              alt={book.title}
            />
            
            {/* Purchase Buttons */}
            <PurchaseSection>
              {renderPurchaseButtons()}
            </PurchaseSection>
          </BookImageContainer>

          {/* Right Column - Book Details */}
          <BookInfo>
            <BookTitle>{book.title}</BookTitle>
            
            {/* Book Metadata */}
            <BookMeta>
              <MetaItem>
                <MetaLabel>Subject</MetaLabel>
                <MetaValue>{book.subject}</MetaValue>
              </MetaItem>
              <MetaItem>
                <MetaLabel>Grade</MetaLabel>
                <MetaValue>{book.grade}</MetaValue>
              </MetaItem>
              <MetaItem>
                <MetaLabel>Vendor</MetaLabel>
                <MetaValue>{book.vendor}</MetaValue>
              </MetaItem>
            </BookMeta>

            {/* Pricing */}
            <PriceContainer>
              <CurrentPrice>
                ${book.discount_price || book.price}
              </CurrentPrice>
              {book.discount_price && (
                <>
                  <OriginalPrice>${book.price}</OriginalPrice>
                  {discount && (
                    <DiscountBadge>{discount}% OFF</DiscountBadge>
                  )}
                </>
              )}
            </PriceContainer>

            {/* Short Description */}
            {book.short_description && (
              <div style={{ 
                padding: '20px',
                background: '#f8fafc',
                borderRadius: '12px',
                marginBottom: '30px',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ 
                  margin: '0 0 10px 0',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Quick Overview
                </h3>
                <p style={{ 
                  margin: 0,
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: '#6b7280'
                }}>
                  {book.short_description}
                </p>
              </div>
            )}

            {/* Full Description */}
            <Description>
              <h3 style={{ 
                fontSize: '1.3rem',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '15px'
              }}>
                Description
              </h3>
              <div dangerouslySetInnerHTML={{ __html: book.description }} />
            </Description>
          </BookInfo>
        </BookDetailContainer>

        {/* Video Section */}
        {book.video_link && (
          <VideoSection>
            <VideoTitle>📺 Preview Video</VideoTitle>
            <VideoContainer>
              <iframe
                width="100%"
                height="100%"
                src={book.video_link}
                title={`${book.title} Video Preview`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '12px'
                }}
              />
            </VideoContainer>
          </VideoSection>
        )}
      </ContentContainer>
    </PageContainer>
    <Footer 
      onBooksClick={onBooksClick}
      onVideoLearningClick={onVideoLearningClick}
      onBlogsClick={onBlogsClick}
      onMkAcademyOnlineClick={onMkAcademyOnlineClick}
      onFaqClick={onFaqClick}
      onFeedbackClick={onFeedbackClick}
    />
    </>
  );
};

export default BookDetailPage;
